import React, { useEffect, useState } from "react";
import { authHeaders, getUser } from "../auth";
import "./HomeBriefing.css";

/* ── HomeBriefing ───────────────────────────────────────────────────────────
   Personalized login home. Two parts (share the same component):
     part="greeting" → "Good morning, <name>" + role   (shown ABOVE the "How can
                        I help?" prompt; uses the JWT, no fetch)
     part="body"     → role-scoped "needs your attention" cards + suggestions
                        (shown BELOW the input; from /home/briefing)
   Renders nothing for signed-out visitors.
   ─────────────────────────────────────────────────────────────────────────── */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function firstName(user) {
  const n = (user && (user.full_name || user.email)) || "";
  return n.split(/[\s@]/)[0] || "there";
}

function HomeBriefing({ apiBase, onPick, part = "body" }) {
  const user = getUser();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (part !== "body") return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/home/briefing`, { headers: { ...authHeaders() } });
        const json = await res.json();
        if (alive) setData(json);
      } catch {
        if (alive) setData({ attention: [], suggestions: [] });
      }
    })();
    return () => { alive = false; };
  }, [apiBase, part]);

  if (!user) return null;

  /* ── Greeting line (above the prompt) ── */
  if (part === "greeting") {
    const role = user.role ? String(user.role).replace(/_/g, " ") : "";
    return (
      <div className="home-greeting">
        <span className="hb-greeting">
          {greeting()}, <span className="hb-name">{firstName(user)}</span>
        </span>
        {role && <span className="hb-role">{role}</span>}
      </div>
    );
  }

  /* ── Body: next-step directions + "needs your attention" cards (below input) ── */
  if (!data) return null;
  const attention = data.attention || [];
  const nextSteps = data.next_steps || [];
  if (attention.length === 0 && nextSteps.length === 0) return null;

  return (
    <div className="home-briefing">
      {nextSteps.length > 0 && (
        <>
          <div className="hb-section-label">Your next steps</div>
          <div className="hb-steps">
            {nextSteps.map((s) => (
              <button
                key={s.key}
                className={`hb-step hb-step-${s.urgency || "low"}`}
                onClick={() => onPick && onPick(s.action || s.title)}
                title={`Open: ${s.action || s.title}`}
              >
                <span className={`hb-step-dot hb-dot-${s.urgency || "low"}`} />
                <span className="hb-step-body">
                  <span className="hb-step-title">{s.title}</span>
                  {s.detail && <span className="hb-step-detail">{s.detail}</span>}
                </span>
                <span className="hb-step-go">→</span>
              </button>
            ))}
          </div>
        </>
      )}

      {attention.length > 0 && (
        <>
          <div className="hb-section-label">Needs your attention</div>
          <div className="hb-cards">
            {attention.map((a) => (
              <button
                key={a.key}
                className="hb-card"
                onClick={() => onPick && onPick(a.title)}
                title={`Ask: ${a.title}`}
              >
                <div className="hb-card-value">{a.value}</div>
                <div className="hb-card-title">{a.title}</div>
                {a.app && <div className="hb-card-app">{a.app}</div>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default HomeBriefing;
